import { InlineLabelInput } from 'components/common';
import { TagsInput } from 'components/common/TagsInput/TagsInput';
import { useState } from 'react';
import {
  PREDEFINED_ANSWER_MAX_LENGTH,
  PREDEFINED_INCORRECT_ANSWERS_MAX_QUANTITY,
  useTaskStore,
} from 'store/task';

export const QuestionValidator = () => {
  const {
    errors,
    draft: draftTask,
    actions: { setDraftQuestionAnswer },
  } = useTaskStore((state) => state);
  const [incorrectValueInput, setIncorrectValueInput] = useState('');
  const [correctAnswerInput, setCorrectAnswerInput] = useState('');

  const { validator, answers, status } = draftTask;
  const isPredefinedOptions = validator === 'PREDEFINED_OPTIONS';

  if (!isPredefinedOptions) {
    return null;
  }

  const correctAnswers = answers?.correctAnswers ?? [];
  const incorrectAnswers = answers?.incorrectAnswers ?? [];

  const pushToCorrect = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const trimmed = correctAnswerInput.trim();

    const isValid =
      trimmed &&
      !correctAnswers.includes(trimmed) &&
      !incorrectAnswers.includes(trimmed) &&
      event.key === 'Enter' &&
      correctAnswers.length === 0 &&
      trimmed.length <= PREDEFINED_ANSWER_MAX_LENGTH;

    if (isValid) {
      setDraftQuestionAnswer({
        correctAnswers: [...correctAnswers, trimmed],
        incorrectAnswers,
      });
      setCorrectAnswerInput('');
    }

    const isBackspacePressedOnEmptyInput =
      event.key === 'Backspace' && correctAnswerInput === '';

    if (isBackspacePressedOnEmptyInput) {
      const tagValue = correctAnswers[correctAnswers.length - 1];

      if (tagValue) {
        const updatedCorrectAnswers = correctAnswers.slice(0, -1);

        setDraftQuestionAnswer({
          correctAnswers: updatedCorrectAnswers,
          incorrectAnswers,
        });
        setCorrectAnswerInput(tagValue);
      }
    }
  };

  const pushToIncorrect = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const trimmed = incorrectValueInput.trim();

    const isValid =
      trimmed &&
      !incorrectAnswers.includes(trimmed) &&
      !correctAnswers.includes(trimmed) &&
      event.key === 'Enter' &&
      incorrectAnswers.length < PREDEFINED_INCORRECT_ANSWERS_MAX_QUANTITY &&
      trimmed.length <= PREDEFINED_ANSWER_MAX_LENGTH;

    if (isValid) {
      setDraftQuestionAnswer({
        correctAnswers,
        incorrectAnswers: [...incorrectAnswers, trimmed],
      });
      setIncorrectValueInput('');
    }

    const isBackspacePressedOnEmptyInput =
      event.key === 'Backspace' && incorrectValueInput === '';

    if (isBackspacePressedOnEmptyInput) {
      const tagValue = incorrectAnswers[incorrectAnswers.length - 1];

      if (tagValue) {
        const updatedIncorrectAnswers = incorrectAnswers.slice(0, -1);

        setDraftQuestionAnswer({
          correctAnswers,
          incorrectAnswers: updatedIncorrectAnswers,
        });
        setIncorrectValueInput(tagValue);
      }
    }
  };

  const filterAnswers = (answers: string[], tag: string): string[] => {
    return answers.filter((answer) => answer !== tag);
  };

  const removeCorrectAnswer = (tag: string): void => {
    setDraftQuestionAnswer({
      correctAnswers: filterAnswers(correctAnswers, tag),
      incorrectAnswers,
    });
  };

  const removeIncorrectAnswer = (tag: string): void => {
    setDraftQuestionAnswer({
      correctAnswers,
      incorrectAnswers: filterAnswers(incorrectAnswers, tag),
    });
  };

  const isNotAvailableStatus =
    status === 'PENDING_APPROVAL' || status === 'APPROVED';

  return (
    <>
      <InlineLabelInput label="Correct answer">
        <TagsInput
          errorMessage={errors.predefinedOptionCorrectAnswer}
          inputValue={correctAnswerInput}
          isDisabled={isNotAvailableStatus}
          isOptionSelected={isPredefinedOptions}
          maxAllowedTags={1}
          maxTagLength={PREDEFINED_ANSWER_MAX_LENGTH}
          pushToList={pushToCorrect}
          removeItemFromList={removeCorrectAnswer}
          tagsList={correctAnswers}
          updateInputValue={setCorrectAnswerInput}
        />
      </InlineLabelInput>
      <InlineLabelInput label="Incorrect answers">
        <TagsInput
          errorMessage={errors.predefinedOptionIncorrectAnswer}
          inputValue={incorrectValueInput}
          isDisabled={isNotAvailableStatus}
          isOptionSelected={isPredefinedOptions}
          maxAllowedTags={PREDEFINED_INCORRECT_ANSWERS_MAX_QUANTITY}
          maxTagLength={PREDEFINED_ANSWER_MAX_LENGTH}
          pushToList={pushToIncorrect}
          removeItemFromList={removeIncorrectAnswer}
          tagsList={incorrectAnswers}
          updateInputValue={setIncorrectValueInput}
        />
      </InlineLabelInput>
    </>
  );
};
